import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { bookingService } from '../services/bookingService';
import { vehicleService } from '../services/vehicleService';
import { customerService } from '../services/customerService';
import { Booking, BookingSummary } from '../types/booking';
import { Vehicle, FleetSummary, VehicleAvailability } from '../types/vehicle';
import { CustomerWithStats, CustomerStats, CustomerFilters } from '../types/customer';

interface DataState {
  // Bookings
  bookings: Booking[];
  bookingSummary: BookingSummary | null;
  bookingsLoaded: boolean;
  bookingsLoading: boolean;

  // Vehicles
  vehicles: Vehicle[];
  fleetSummary: FleetSummary | null;
  vehicleAvailability: VehicleAvailability[];
  vehiclesLoaded: boolean;
  vehiclesLoading: boolean;

  // Customers
  customers: CustomerWithStats[];
  customerStats: CustomerStats | null;
  customerTotal: number;
  customersLoaded: boolean;
  customersLoading: boolean;

  // Actions
  refreshBookings: (filters?: any) => Promise<void>;
  refreshVehicles: () => Promise<void>;
  refreshCustomers: (filters?: CustomerFilters) => Promise<void>;
  refreshAll: () => Promise<void>;

  // Last updated timestamps
  bookingsLastUpdated: Date | null;
  vehiclesLastUpdated: Date | null;
  customersLastUpdated: Date | null;
}

const DataContext = createContext<DataState | undefined>(undefined);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // --- Bookings ---
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [bookingSummary, setBookingSummary] = useState<BookingSummary | null>(null);
  const [bookingsLoaded, setBookingsLoaded] = useState(false);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsLastUpdated, setBookingsLastUpdated] = useState<Date | null>(null);
  const lastBookingFiltersRef = useRef<string>('');

  // --- Vehicles ---
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [fleetSummary, setFleetSummary] = useState<FleetSummary | null>(null);
  const [vehicleAvailability, setVehicleAvailability] = useState<VehicleAvailability[]>([]);
  const [vehiclesLoaded, setVehiclesLoaded] = useState(false);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [vehiclesLastUpdated, setVehiclesLastUpdated] = useState<Date | null>(null);

  // --- Customers ---
  const [customers, setCustomers] = useState<CustomerWithStats[]>([]);
  const [customerStats, setCustomerStats] = useState<CustomerStats | null>(null);
  const [customerTotal, setCustomerTotal] = useState(0);
  const [customersLoaded, setCustomersLoaded] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersLastUpdated, setCustomersLastUpdated] = useState<Date | null>(null);
  const lastCustomerFiltersRef = useRef<string>('');

  // --- Refresh Bookings ---
  const refreshBookings = useCallback(async (filters?: any) => {
    const filterKey = JSON.stringify(filters || {});
    const isNewFilter = filterKey !== lastBookingFiltersRef.current;
    lastBookingFiltersRef.current = filterKey;

    if (!bookingsLoaded || isNewFilter) {
      setBookingsLoading(true);
    }
    try {
      // Build date range from filters or default to current month
      const now = new Date();
      const defaultStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const defaultEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

      const dateFilters = {
        ...filters,
        startDate: filters?.startDate || defaultStart,
        endDate: filters?.endDate || defaultEnd,
      };

      const dashboardRes = await bookingService.getBookingDashboardData({
        ...dateFilters,
        page: filters?.page || 1,
        limit: filters?.limit || 20,
        lightweight: true,
      });
      const bookingsRes = dashboardRes.success && dashboardRes.data
        ? { success: true, data: dashboardRes.data.list }
        : await bookingService.getBookingsPage({
            ...dateFilters,
            page: filters?.page || 1,
            limit: filters?.limit || 20,
            lightweight: true,
          });

      if (bookingsRes.success && bookingsRes.data) {
        setBookings(bookingsRes.data.items || []);
      }
      if (dashboardRes.success && dashboardRes.data?.summary) {
        setBookingSummary(dashboardRes.data.summary);
      }
      setBookingsLoaded(true);
      setBookingsLastUpdated(new Date());
    } catch (err) {
      console.warn('DataContext: refreshBookings failed', err);
    } finally {
      setBookingsLoading(false);
    }
  }, [bookingsLoaded]);

  // --- Refresh Vehicles ---
  const refreshVehicles = useCallback(async () => {
    if (!vehiclesLoaded) {
      setVehiclesLoading(true);
    }
    try {
      const dashboardRes = await vehicleService.getFleetDashboardData();
      if (dashboardRes.success && dashboardRes.data) {
        setVehicles(dashboardRes.data.vehicles || []);
        setFleetSummary(dashboardRes.data.summary);
        setVehicleAvailability(dashboardRes.data.availability || []);
      } else {
        const [vehiclesRes, summaryRes, availRes] = await Promise.all([
          vehicleService.getAllVehicles(),
          vehicleService.getFleetSummary(),
          vehicleService.getVehiclesAvailability(),
        ]);
        if (vehiclesRes.success && vehiclesRes.data) setVehicles(vehiclesRes.data);
        if (summaryRes.success && summaryRes.data) setFleetSummary(summaryRes.data);
        if (availRes.success && availRes.data) setVehicleAvailability(availRes.data);
      }
      setVehiclesLoaded(true);
      setVehiclesLastUpdated(new Date());
    } catch (err) {
      console.warn('DataContext: refreshVehicles failed', err);
    } finally {
      setVehiclesLoading(false);
    }
  }, [vehiclesLoaded]);

  // --- Refresh Customers ---
  const refreshCustomers = useCallback(async (filters?: CustomerFilters) => {
    const filterKey = JSON.stringify(filters || {});
    const isNewFilter = filterKey !== lastCustomerFiltersRef.current;
    lastCustomerFiltersRef.current = filterKey;

    if (!customersLoaded || isNewFilter) {
      setCustomersLoading(true);
    }
    try {
      const customersRequest = customerService.getCustomersWithStats(filters || {});
      const statsRequest = customerService.getCustomerStats();
      const customersRes = await customersRequest;
      setCustomers(customersRes.customers || []);
      setCustomerTotal(customersRes.total || 0);
      setCustomersLoaded(true);
      setCustomersLastUpdated(new Date());
      setCustomersLoading(false);

      const statsRes = await statsRequest;
      setCustomerStats(statsRes);
    } catch (err) {
      console.warn('DataContext: refreshCustomers failed', err);
    } finally {
      setCustomersLoading(false);
    }
  }, [customersLoaded]);

  // --- Refresh All ---
  const refreshAll = useCallback(async () => {
    await Promise.all([
      refreshBookings(),
      refreshVehicles(),
      refreshCustomers(),
    ]);
  }, [refreshBookings, refreshVehicles, refreshCustomers]);

  // --- Auto-refresh: poll every 30s + tab focus ---
  useEffect(() => {
    const silentRefresh = () => {
      if (document.visibilityState !== 'visible') return;
      if (bookingsLoaded) refreshBookings();
      if (vehiclesLoaded) refreshVehicles();
      if (customersLoaded) refreshCustomers();
    };

    const pollInterval = setInterval(silentRefresh, 30000);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        silentRefresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [bookingsLoaded, vehiclesLoaded, customersLoaded, refreshBookings, refreshVehicles, refreshCustomers]);

  const value: DataState = {
    bookings, bookingSummary, bookingsLoaded, bookingsLoading,
    vehicles, fleetSummary, vehicleAvailability, vehiclesLoaded, vehiclesLoading,
    customers, customerStats, customerTotal, customersLoaded, customersLoading,
    refreshBookings, refreshVehicles, refreshCustomers, refreshAll,
    bookingsLastUpdated, vehiclesLastUpdated, customersLastUpdated,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export default DataContext;
